// Client-side derivation for the guided "Book a court" flow.
//
// The availability detail endpoint already returns every court with its
// hourly slots (`available` is the source-of-truth bookability flag). The
// guided form derives sport → time → court entirely from that payload, so
// no extra endpoint is needed. Slot starts are always whole-hour boundaries
// (backend SLOT_MINUTES = 60 + reserve `start_must_be_hourly`).

import type { AvailabilityCourt, CourtType } from '../../types/CourtRental'

export type Duration = 60 | 90 | 120

export const DURATIONS: Duration[] = [60, 90, 120]

export interface SportMeta {
  key: CourtType
  label: string
}

// Figma order: Tennis, Pickleball, Padel.
export const SPORT_META: SportMeta[] = [
  { key: 'tennis', label: 'Tennis' },
  { key: 'pickle', label: 'Pickleball' },
  { key: 'padel', label: 'Padel' },
]

export interface TimeOption {
  startUtc: string
  label: string
  available: boolean
  // Coach lesson "soft reserve" holds are an internal/admin concept and are
  // never surfaced in the customer booking flow, so this is always false here.
  // Kept on the type for backward compatibility with downstream props.
  softReserve: boolean
  // Always null in the customer flow (soft-reserve waitlist join is not exposed).
  lessonSlotId: string | null
}

// Sub-palette mirrors CourtHourMatrix so the sport accent stays consistent.
export const SPORT_COLOR: Record<CourtType, string> = {
  pickle: '#FF7A1A',
  tennis: '#06C270',
  padel: '#1A8CFF',
}

// Whole hourly slots a booking spans: 60 → 1, 90/120 → 2.
export const hoursNeeded = (duration: Duration): number => Math.ceil(duration / 60)

export const formatVenueHour = (utcIso: string, venueTimezone: string): string => {
  const d = new Date(utcIso)
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: venueTimezone }).format(d)
}

// Sports the venue actually offers, in canonical order.
export const deriveSports = (courts: AvailabilityCourt[]): CourtType[] => {
  const present = new Set(courts.map((c) => c.court_type))
  return SPORT_META.map((s) => s.key).filter((k) => present.has(k))
}

const sportCourts = (courts: AvailabilityCourt[], sport: CourtType): AvailabilityCourt[] =>
  courts.filter((c) => c.court_type === sport)

// Distinct hourly starts across the sport's courts. A start is `available`
// when at least one of the sport's courts has that hour free.
//
// Past starts on the current day are filtered out: a slot whose start instant
// is at or before `now` is no longer bookable. `start_utc` is an absolute
// instant and `nowMs` is the absolute current instant, so this comparison is
// timezone-safe (it honours venue-local "now" implicitly) without any
// date-string math — never toISOString() (XC-02). The date picker only offers
// today forward, so in practice this only trims already-passed hours today.
export const deriveTimes = (
  courts: AvailabilityCourt[],
  sport: CourtType,
  venueTimezone: string,
  nowMs: number = Date.now(),
): TimeOption[] => {
  const byStart = new Map<string, { available: boolean }>()
  for (const court of sportCourts(courts, sport)) {
    for (const slot of court.slots) {
      // Drop hours that have already started.
      if (new Date(slot.start_utc).getTime() <= nowMs) continue
      const prev = byStart.get(slot.start_utc) ?? { available: false }
      const available = prev.available || slot.available === true
      byStart.set(slot.start_utc, { available })
    }
  }
  return Array.from(byStart.entries())
    .map(([startUtc, { available }]) => ({
      startUtc,
      available,
      // Soft-reserve / waitlist is never surfaced to the booking customer.
      softReserve: false,
      lessonSlotId: null,
      label: formatVenueHour(startUtc, venueTimezone),
    }))
    .sort((a, b) => a.startUtc.localeCompare(b.startUtc))
}

// Courts of the sport that can host `duration` starting at `startUtc` — i.e.
// the start hour plus every subsequent hour the duration spans are all free.
export const deriveCourts = (
  courts: AvailabilityCourt[],
  sport: CourtType,
  startUtc: string,
  duration: Duration,
): AvailabilityCourt[] => {
  const span = hoursNeeded(duration)
  return sportCourts(courts, sport).filter((court) => {
    const idx = court.slots.findIndex((s) => s.start_utc === startUtc)
    if (idx === -1) return false
    for (let i = 0; i < span; i += 1) {
      const slot = court.slots[idx + i]
      if (!slot || slot.available !== true) return false
    }
    return true
  })
}
